import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Tag, Trash2, Plus, MapPin } from "lucide-react";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useAuth } from "@/context/AuthContext";
import { listRates, createRate, deleteRate } from "@/lib/api";

const empty = {
  region: "", pincode: "", material: "",
  material_rate_inr_per_kg: "", labour_rate_inr_per_hr: "", machine_rate_inr_per_hr: "",
};

export default function RateLibrary() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [rates, setRates] = useState([]);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { setRates(await listRates()); } catch { /* silent */ }
  };

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/"); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading]);

  const num = (v) => (v === "" || v === null ? null : Number(v));

  const save = async () => {
    if (form.pincode && !/^\d{6}$/.test(form.pincode)) return toast.error("Pincode must be 6 digits (or blank).");
    if (!form.material_rate_inr_per_kg && !form.labour_rate_inr_per_hr && !form.machine_rate_inr_per_hr) {
      return toast.error("Enter at least one rate.");
    }
    setSaving(true);
    try {
      await createRate({
        region: form.region,
        pincode: form.pincode,
        material: form.material,
        material_rate_inr_per_kg: num(form.material_rate_inr_per_kg),
        labour_rate_inr_per_hr: num(form.labour_rate_inr_per_hr),
        machine_rate_inr_per_hr: num(form.machine_rate_inr_per_hr),
      });
      toast.success("Rate saved.");
      setForm(empty);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save rate.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await deleteRate(id);
      setRates((r) => r.filter((x) => x.id !== id));
      toast.success("Rate removed.");
    } catch {
      toast.error("Could not remove rate.");
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const numSet = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value.replace(/[^\d.]/g, "") }));

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="mx-auto max-w-5xl px-5 py-8 sm:px-8">
        <button
          data-testid="rates-back"
          onClick={() => navigate("/")}
          className="mb-4 inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.15em] text-muted-foreground transition-colors hover:text-primary"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back
        </button>
        <div className="mb-2 flex items-center gap-3">
          <Tag className="h-6 w-6 text-primary" />
          <h1 className="font-display text-3xl font-black tracking-tight">Rate Library</h1>
        </div>
        <p className="mb-6 max-w-2xl text-sm text-muted-foreground">
          Save your own supplier material &amp; labour rates by region. When you run an estimate for a
          matching pincode, these rates are auto-applied so costs match your actual suppliers.
        </p>

        {/* Add form */}
        <div className="mb-8 rounded-sm border border-border bg-card p-5" data-testid="rate-form">
          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <Label className="font-mono text-[11px] uppercase tracking-[0.15em] text-muted-foreground">Region</Label>
              <Input data-testid="rate-region" className="mt-1.5" placeholder="e.g. Pune" value={form.region} onChange={set("region")} />
            </div>
            <div>
              <Label className="font-mono text-[11px] uppercase tracking-[0.15em] text-muted-foreground">Pincode</Label>
              <div className="relative mt-1.5">
                <MapPin className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input data-testid="rate-pincode" className="pl-9 font-mono" maxLength={6} placeholder="411019" value={form.pincode} onChange={(e) => setForm((f) => ({ ...f, pincode: e.target.value.replace(/\D/g, "") }))} />
              </div>
            </div>
            <div>
              <Label className="font-mono text-[11px] uppercase tracking-[0.15em] text-muted-foreground">Material</Label>
              <Input data-testid="rate-material" className="mt-1.5" placeholder="e.g. EN8" value={form.material} onChange={set("material")} />
            </div>
            <div>
              <Label className="font-mono text-[11px] uppercase tracking-[0.15em] text-muted-foreground">Material ₹/kg</Label>
              <Input data-testid="rate-matrate" className="mt-1.5 font-mono" inputMode="decimal" placeholder="92" value={form.material_rate_inr_per_kg} onChange={numSet("material_rate_inr_per_kg")} />
            </div>
            <div>
              <Label className="font-mono text-[11px] uppercase tracking-[0.15em] text-muted-foreground">Labour ₹/hr</Label>
              <Input data-testid="rate-labour" className="mt-1.5 font-mono" inputMode="decimal" placeholder="180" value={form.labour_rate_inr_per_hr} onChange={numSet("labour_rate_inr_per_hr")} />
            </div>
            <div>
              <Label className="font-mono text-[11px] uppercase tracking-[0.15em] text-muted-foreground">Machine ₹/hr</Label>
              <Input data-testid="rate-machine" className="mt-1.5 font-mono" inputMode="decimal" placeholder="520" value={form.machine_rate_inr_per_hr} onChange={numSet("machine_rate_inr_per_hr")} />
            </div>
          </div>
          <Button data-testid="save-rate-button" onClick={save} disabled={saving} className="mt-5 gap-2 rounded-sm">
            <Plus className="h-4 w-4" /> Save rate
          </Button>
        </div>

        {/* Saved rates */}
        {rates.length === 0 ? (
          <div className="rounded-sm border border-dashed border-border p-12 text-center" data-testid="rates-empty">
            <p className="text-sm text-muted-foreground">No saved rates yet. Add your first supplier rate above.</p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-sm border border-border" data-testid="rates-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Material</TableHead>
                  <TableHead>Region</TableHead>
                  <TableHead>Pincode</TableHead>
                  <TableHead className="text-right">Material ₹/kg</TableHead>
                  <TableHead className="text-right">Labour ₹/hr</TableHead>
                  <TableHead className="text-right">Machine ₹/hr</TableHead>
                  <TableHead className="text-right">Remove</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rates.map((r) => (
                  <TableRow key={r.id} data-testid={`rate-row-${r.id}`}>
                    <TableCell className="font-medium">{r.material || "—"}</TableCell>
                    <TableCell>{r.region || "—"}</TableCell>
                    <TableCell className="font-mono">{r.pincode || "—"}</TableCell>
                    <TableCell className="text-right font-mono">{r.material_rate_inr_per_kg ?? "—"}</TableCell>
                    <TableCell className="text-right font-mono">{r.labour_rate_inr_per_hr ?? "—"}</TableCell>
                    <TableCell className="text-right font-mono">{r.machine_rate_inr_per_hr ?? "—"}</TableCell>
                    <TableCell className="text-right">
                      <button
                        data-testid={`delete-rate-${r.id}`}
                        onClick={() => remove(r.id)}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-sm text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                        title="Remove"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
